/**
 * Professor Ka's artwork and the metrics of the chrome it sits in — the ONE
 * place either platform reads them from.
 *
 * Both clients animate the same "Ka writing" LottieFiles composition, but they
 * cannot load it the same way: `lottie-react-native` wants a parsed JSON object
 * and `@lottiefiles/dotlottie-react` wants a `.lottie` URL. That renderer split
 * is real and is the only thing the two platform files fork over — everything
 * ELSE about the avatar (which artwork, how big, how it is cropped, what it is
 * called to a screen reader, when it is allowed to animate) lives here, because
 * every one of those had already drifted while the mascot was written twice.
 */

/**
 * The hosted `.lottie` of the composition, streamed by WEB.
 *
 * NATIVE instead bundles the SAME composition as a dotLottie asset —
 * `./professor-ka.lottie`, loaded by `ProfessorKaAvatar.data.native.ts`
 * (a platform-split file, because a module-scope `require` cannot appear in
 * anything the browser executes). The split keeps the asset out of the web
 * bundle and the startup network fetch off the native critical path.
 * Keep the two in step: re-export from LottieFiles and re-vendor TOGETHER if
 * design ever revs the artwork.
 */
export const PROFESSOR_KA_LOTTIE_URL =
  'https://lottie.host/0dcf45ea-6b16-4a51-85ee-5c978b52f947/Jw2v3F5JMe.lottie';

/** Screen-reader name for the mascot on both platforms. */
export const PROFESSOR_KA_LABEL = 'Professor Ka';

/**
 * The floating entry tile's square edge in DS points (Figma `130:4865`) — the
 * same 3.5rem box as the create FAB it sits beside, so the pair reads as one
 * row. NOT the rendered size on a tablet: see {@link professorKaTileMetrics}.
 */
export const KA_TILE_SIZE = 56;

/**
 * The artwork's framing inside the tile: the vendored composition rendered at
 * EXACTLY the tile's size, seated flush with its BORDER box (offset −border).
 *
 * CALIBRATED, not read off the Figma layer — the design node (`180:18965`)
 * draws ITS animation at 1.77× the tile, but that LottieFiles export carries
 * far more canvas padding than our vendored `professor-ka` JSON. Solving the
 * affine map between the design render and a device render via facial
 * landmarks (eyes / brows / mouth) gives scale ≈0.99 of the tile at ≈−1pt
 * offset for OUR composition — i.e. border-box fill. See the trap in
 * `mobile-product-docs/resources/platform-traps.md`.
 *
 * A RATIO OF THE TILE, not a second hard-coded pixel size, and that is the
 * point: the tile is rem-sized and ramps to 70pt on a landscape iPad
 * (`useResponsiveScale`), and both wrong framings this replaces were absolute
 * sizes that could not ramp with it — `size={60}` was 1.07× on a phone (the
 * "icon does not match the design" report, 2026-08-25) and 0.86×
 * boxed-with-margin on the iPad ("the ka professor on iPad is wrong",
 * 2026-08-26).
 */
export const KA_TILE_ART_RATIO = 1;

/** Tile corner radius at scale 1 (Figma `130:4865`). */
export const KA_TILE_RADIUS = 20;

/** Tile ring width at scale 1 — the frame's 2.15px amber stroke. */
export const KA_TILE_BORDER = 2.15;

/** Every geometry value of the entry tile at a given DS scale. */
export interface ProfessorKaTileMetrics {
  /** Rendered edge of the tile — matches the FAB beside it. */
  size: number;
  /** Rendered edge of the artwork (the tile's border box — see the ratio). */
  art: number;
  /**
   * The artwork's top/left offset INSIDE the tile. `−border`, so the
   * composition spans the border box: a (0, 0) child sits inside the padding
   * box and loses ~2pt off the right and bottom edges to the ring.
   */
  artOffset: number;
  radius: number;
  border: number;
}

/**
 * Resolve the tile's geometry for a DS scale (`useResponsiveScale()`: 1 on web
 * and on phones, up to 1.25 on a landscape iPad).
 *
 * ALL FOUR values ramp together. Scaling only some of them is what produced the
 * iPad regression above, and a single function means a future change cannot fix
 * one and forget another.
 *
 * `sizeOverride` — the RENDERED edge, already in final points, for a host that
 * pins the tile to a designed size instead of the ramp (the iPad floating tile
 * renders ~36pt beside taller controls). Every other value still derives from
 * the effective size, so the ring, radius and art framing keep their
 * calibrated proportions at any override.
 */
export function professorKaTileMetrics(
  scale: number,
  sizeOverride?: number,
): ProfessorKaTileMetrics {
  const size = sizeOverride ?? KA_TILE_SIZE * scale;
  const factor = size / KA_TILE_SIZE;
  const border = KA_TILE_BORDER * factor;
  return {
    size,
    art: size * KA_TILE_ART_RATIO,
    artOffset: -border,
    radius: KA_TILE_RADIUS * factor,
    border,
  };
}

/**
 * How many times the mascot plays its 5-second "Ka writing" loop before it
 * rests on its (seamless) first frame — on BOTH platforms.
 *
 * It used to loop for as long as it was mounted, and it is mounted on every
 * ordinary screen (the floating tile over Home, Library, the deck screen, a
 * study group): a 60 fps, ~20-layer composition drawn for the whole session.
 * QA 2026-10 (#35, "1.5 h took the battery from 60% to 14%") traced the
 * steady idle CPU to it. A few plays still greet the student when a screen
 * opens; after that the tile is a still picture. Native also replays a fresh
 * round when the app returns to the foreground.
 */
export const KA_IDLE_PLAYS = 3;
